<template>
    <div class="history-form" :class="{ 'with-styling': styling }">
        <div class="header">
            <transition name="fade" duration="500" mode="out-in" tag="div">
                <div v-if="!success" key="1">
                    <h2 class="mt-0 mb-2">{{ translate('order.history.form.title') }}</h2>
                    <p>{{ translate('order.history.form.description') }}</p>
                </div>
                <div v-else key="2">
                    <h2 class="mt-0 mb-2">{{ translate('order.history.form.title.success') }}</h2>
                    <p>{{ translate('order.history.description.success', { email: form.email }) }}</p>
                    <div class="link-to-modal" @click="openInfoModal()">{{ translate('order.history.noEmail') }}</div>
                </div>
            </transition>
        </div>
        <transition name="fadeHeight" mode="out-in">
            <div v-if="!success" class="content">
                <UIForm ref="contactForm" class="form horizontal" :class="{ horizontal: horizontal }" novalidate>
                    <label for="email" class="font-weight-bold mb-1">{{ translate('contact.email') }}</label>
                    <span class="required ml-1">({{ translate('required') }})</span>
                    <div class="input-wrapper">
                        <UIFormInput
                            id="email"
                            v-model="form.email"
                            name="email"
                            type="email"
                            required
                            autocorrect="off"
                            validation="email"
                        >
                            <template #error-message>
                                {{ translate('email.error') }}
                            </template>
                            <template #warning-message>
                                {{ translate('email.warning') }}
                            </template>
                        </UIFormInput>
                        <UIButton type="submit" class="form-button" variant="primary" @click.prevent="onSubmit">{{
                            translate('button.submit')
                        }}</UIButton>
                    </div>
                </UIForm>
                <UIAlert v-if="errorMessage" variant="warning">
                    <span>{{ errorMessage }}</span>
                </UIAlert>
            </div>
        </transition>
    </div>
</template>

<script>
import { UIForm, UIFormInput, UIButton } from '@dundle/ui/components';
import { mapActions } from 'vuex';
import TranslationMixin from '~/mixins/TranslationMixin';
export default {
    components: {
        UIForm,
        UIFormInput,
        UIButton,
    },
    mixins: [TranslationMixin],
    props: {
        horizontal: {
            type: Boolean,
            default: false,
        },
        styling: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: {
                email: '',
            },
            show: true,
            success: false,
            errorMessage: '',
        };
    },
    async mounted() {
        try {
            await this.$recaptcha.init();
        } catch (e) {
            console.error(e);
        }
    },
    beforeDestroy() {
        this.$recaptcha.destroy();
    },
    methods: {
        ...mapActions({
            showModal: 'ui/showModal',
        }),
        openInfoModal() {
            this.showModal({
                name: 'info',
                data: {
                    title: this.translate('order.history.noEmail'),
                    message: this.translate('order.history.noEmail.description'),
                    button: this.translate('button.ok'),
                },
            });
        },
        async onSubmit() {
            if (!this.$validation.validateForm(this.$refs.contactForm)) return false;

            this.$gtm?.push({ event: 'event_order_history_button' });
            await this.$user.login('guest', this.form.email);
            try {
                const recaptchaToken = await this.$recaptcha.execute('sendOrderHistoryEmail');
                await this.$api.order.orderHistoryMail(recaptchaToken);
            } catch (error) {
                this.errorMessage = error.response.data.message;
                this.success = false;
                return false;
            }

            this.success = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.link-to-modal {
    color: $color-link;
    text-decoration: underline;
    &:hover {
        cursor: pointer;
    }
}
.history-form {
    width: 100%;
    border-radius: $border-radius-default;
    overflow: hidden;
    &.with-styling {
        .header {
            color: white !important;
            padding: 2rem 1rem;
            background: linear-gradient(-53deg, #009bd2, #007cd2);
            h2 {
                color: $color-text-inverted;
            }
        }
        .content {
            border-radius: 0 0 $border-radius-default $border-radius-default;
            border: 1px solid $color-grey-3;
            padding: 1rem;
        }
        .link-to-modal {
            color: $color-text-inverted;
        }
    }
}
.form {
    .form-button {
        min-width: 100%;
        margin-top: 1rem;
    }
    &.horizontal {
        @media only screen and (min-width: 1024px) {
            .input-wrapper {
                display: flex;
                flex-wrap: nowrap;
            }
            .form-button {
                margin-top: 0;
                margin-left: 1rem;
                min-width: 30%;
                height: 3rem;
            }
        }
    }
}
.fadeHeight-enter-active,
.fadeHeight-leave-active {
    transition: all 0.5s;
    max-height: 230px;
}
.fadeHeight-enter,
.fadeHeight-leave-to {
    opacity: 0;
    max-height: 0px;
}
</style>

<i18n src="@dundle/locale/data/buttons.json"></i18n>
<i18n src="@dundle/locale/data/keywords.json"></i18n>
<i18n src="@dundle/locale/data/validation.json"></i18n>
<i18n src="@dundle/locale/data/order.json"></i18n>
